import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/auth/auth-provider';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { PlaybookCatalogue, canRunTier } from '@/components/fleet/playbook-catalogue';
import { PlaybookTaskDrawer } from '@/components/fleet/playbook-task-drawer';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { SurveyForm } from '@/components/fleet/survey-form';
import { TaskHistory } from '@/components/fleet/task-history';
import { TierBadge } from '@/components/fleet/tier-badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useProvisioningPlaybooks } from '@/hooks/use-provisioning-playbooks';
import { useRunPlaybook } from '@/hooks/use-run-playbook';
import type { Playbook } from '@/types/provisioning';

/** The selected-playbook run panel — survey form + submit. */
function RunPanel({
  playbook,
  isAdmin,
  onRan,
}: {
  playbook: Playbook;
  isAdmin: boolean;
  onRan: (taskId: number, name: string) => void;
}): JSX.Element {
  const runMutation = useRunPlaybook();
  const runnable = canRunTier(playbook.tier, isAdmin);
  const isDestructive = playbook.tier === 'DESTRUCTIVE';

  // For DESTRUCTIVE playbooks the survey form's submit only collects the args;
  // a second confirm dialog gates the actual run on a mandatory reason +
  // type-the-playbook-name-to-confirm.
  const [pendingArgs, setPendingArgs] = useState<Record<string, unknown> | null>(null);

  function fire(args: Record<string, unknown>, reason?: string): void {
    runMutation.mutate(
      { playbookId: playbook.id, body: reason ? { args, reason } : { args } },
      {
        onSuccess: ({ task_id }) => {
          toast.success(`${playbook.name} queued (task #${task_id}).`);
          onRan(task_id, playbook.name);
        },
        onError: (err) => toast.error(`Couldn't start ${playbook.name}: ${err.message}`),
      },
    );
  }

  function submit(args: Record<string, unknown>): void {
    if (isDestructive) {
      // Defer the run — open the destructive confirm dialog instead.
      setPendingArgs(args);
      return;
    }

    fire(args);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{playbook.name}</CardTitle>
          <TierBadge tier={playbook.tier} />
        </div>
        {playbook.description && (
          <p className="text-sm text-muted-foreground">{playbook.description}</p>
        )}
      </CardHeader>
      <CardContent>
        {runnable ? (
          <>
            {isDestructive && (
              <p className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                This is a DESTRUCTIVE playbook. You will be asked to confirm with
                a reason and by typing the playbook name.
              </p>
            )}
            <SurveyForm
              playbook={playbook}
              submitting={runMutation.isPending}
              onSubmit={submit}
            />
          </>
        ) : (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            This playbook is tier {playbook.tier} and requires the admin role.
          </p>
        )}
      </CardContent>

      <ConfirmDialog
        open={pendingArgs !== null}
        onOpenChange={(open) => {
          if (!open) setPendingArgs(null);
        }}
        title={`Run ${playbook.name}`}
        description={`This is a DESTRUCTIVE operation. ${
          playbook.description ?? ''
        } It cannot be undone — confirm carefully.`}
        confirmLabel={`Run ${playbook.name}`}
        variant="destructive"
        reasonRequired
        confirmPhrase={playbook.name}
        pending={runMutation.isPending}
        onConfirm={(reason) => {
          if (pendingArgs) {
            fire(pendingArgs, reason);
            setPendingArgs(null);
          }
        }}
      />
    </Card>
  );
}

/**
 * `/fleet/playbooks` — the provisioning runner. Left: the playbook catalogue
 * grouped by `group` with tier badges. Right: a run panel that renders the
 * selected playbook's survey as a form. Submitting opens a task drawer that
 * polls the task and live-tails its log. A second tab shows task history.
 */
export function FleetPlaybooksPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const playbooksQuery = useProvisioningPlaybooks();

  const [selected, setSelected] = useState<Playbook | null>(null);
  const [tab, setTab] = useState<'run' | 'history'>('run');
  const [drawerTask, setDrawerTask] = useState<{ id: number; name: string } | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Playbooks</h1>
        <p className="text-sm text-muted-foreground">
          Run any infrastructure operation through the Provisioning API.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'run' | 'history')}>
        <TabsList>
          <TabsTrigger value="run">Run</TabsTrigger>
          <TabsTrigger value="history">Task history</TabsTrigger>
        </TabsList>

        <TabsContent value="run" className="pt-4">
          {playbooksQuery.isLoading && <TableSkeleton rows={8} />}

          {playbooksQuery.isError && (
            <ErrorState
              title="Couldn't load the playbook catalogue."
              error={playbooksQuery.error}
              onRetry={() => void playbooksQuery.refetch()}
            />
          )}

          {playbooksQuery.data && playbooksQuery.data.length === 0 && (
            <EmptyState message="No playbooks are registered." />
          )}

          {playbooksQuery.data && playbooksQuery.data.length > 0 && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px,1fr]">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Catalogue</CardTitle>
                </CardHeader>
                <CardContent>
                  <PlaybookCatalogue
                    playbooks={playbooksQuery.data}
                    selectedId={selected?.id ?? null}
                    isAdmin={isAdmin}
                    onSelect={setSelected}
                  />
                </CardContent>
              </Card>

              {selected ? (
                <RunPanel
                  key={selected.id}
                  playbook={selected}
                  isAdmin={isAdmin}
                  onRan={(id, name) => setDrawerTask({ id, name })}
                />
              ) : (
                <Card>
                  <CardContent className="py-16">
                    <EmptyState message="Select a playbook from the catalogue to configure a run." />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="pt-4">
          <Card>
            <CardContent className="pt-6">
              <TaskHistory
                onSelectTask={(id, name) => setDrawerTask({ id, name })}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PlaybookTaskDrawer
        taskId={drawerTask?.id ?? null}
        playbookName={drawerTask?.name ?? null}
        onClose={() => setDrawerTask(null)}
      />
    </div>
  );
}
